import React, { useEffect, useState } from 'react';
import { X, CheckCircle2Icon } from 'lucide-react';
import { Button } from './ui/Button';
import { useConsultation } from './ConsultationContext';
export function ConsultationModal() {
  const {
    isModalOpen,
    closeModal
  } = useConsultation();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consentTransactional: false,
    consentMarketing: false
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.firstName && formData.lastName && formData.email && formData.phone && formData.consentTransactional) {
      // Send data to webhook
      try {
        await fetch('https://services.leadconnectorhq.com/hooks/DBw1tkqBTZflFworLNDq/webhook-trigger/99a04f8d-93f8-4b99-b3b2-11f91e4dcd78', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            consentTransactional: formData.consentTransactional,
            consentMarketing: formData.consentMarketing,
            source: 'Glass Skin Mesotherapy Landing Page'
          })
        });
      } catch {
        // Continue to thank you screen even if webhook fails
      }
      setSubmitted(true);
    }
  };
  // Fire Google conversion tag when thank you screen loads
  useEffect(() => {
    if (submitted && window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-830901894'
      });
    }
  }, [submitted]);
  const handleClose = () => {
    closeModal();
    // Reset after close with a small delay so animation completes
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        consentTransactional: false,
        consentMarketing: false
      });
    }, 200);
  };
  const handleGoToCalendly = () => {
    window.open('https://calendly.com/be_clinical/30min', '_blank');
    handleClose();
  };
  if (!isModalOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-id="element-43">
      <div className="bg-cream w-full max-w-lg rounded-xl shadow-2xl overflow-hidden relative max-h-[90vh] flex flex-col" data-id="element-44">
        {submitted /* Thank You Screen */ ? <div className="p-8 flex flex-col items-center text-center" data-id="element-45">
            <button onClick={handleClose} className="absolute top-4 right-4 text-foreground/40 hover:text-primary transition-colors p-1" aria-label="Close modal" data-id="element-46">
              <X size={22} data-id="element-47" />
            </button>

            <div className="w-16 h-16 rounded-full bg-jade/20 flex items-center justify-center mb-6" data-id="element-48">
              <CheckCircle2Icon className="w-9 h-9 text-jade" data-id="element-49" />
            </div>

            <h3 className="font-serif text-3xl text-primary mb-3" data-id="element-50">
              Thank You, {formData.firstName}!
            </h3>
            <p className="text-foreground/70 mb-2" data-id="element-51">
              We're excited to help you achieve your glass skin goals.
            </p>
            <p className="text-sm text-foreground/50 mb-8" data-id="element-52">
              Click below to schedule your consultation, or we'll reach out to
              you shortly.
            </p>

            <Button onClick={handleGoToCalendly} className="mb-4" data-id="element-53">
              Schedule a Free Consultation
            </Button>

            <button onClick={handleClose} className="text-sm text-foreground/50 hover:text-foreground transition-colors" data-id="element-54">
              Close
            </button>
          </div> /* Form Screen */ : <>
            <div className="p-6 pb-5 border-b border-divider bg-cream-dark/30 shrink-0 relative" data-id="element-55">
              <button onClick={handleClose} className="absolute top-4 right-4 text-foreground/40 hover:text-primary transition-colors p-1" aria-label="Close modal" data-id="element-56">
                <X size={22} data-id="element-57" />
              </button>
              <h3 className="font-serif text-3xl text-primary mb-3" data-id="element-58">
                Book Consultation
              </h3>
              <div className="flex items-start gap-2 text-foreground/60" data-id="element-59">
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24" data-id="element-60">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z" data-id="element-61" />
                </svg>
                <div data-id="element-62">
                  <p className="text-sm font-medium text-foreground/70" data-id="element-63">
                    Be Clinical Med Spa in San Ramon, CA
                  </p>
                  <p className="text-sm text-foreground/50" data-id="element-64">
                    Off of 680 Freeway and Crow Canyon Road
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto" data-id="element-65">
              <form onSubmit={handleSubmit} className="space-y-5" data-id="element-66">
                <div className="grid grid-cols-2 gap-4" data-id="element-67">
                  <div className="space-y-1" data-id="element-68">
                    <label htmlFor="firstName" className="text-sm font-medium text-foreground/80" data-id="element-69">
                      First Name *
                    </label>
                    <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-2 bg-white border border-divider rounded-md focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition-colors" data-id="element-70" />
                  </div>
                  <div className="space-y-1" data-id="element-71">
                    <label htmlFor="lastName" className="text-sm font-medium text-foreground/80" data-id="element-72">
                      Last Name *
                    </label>
                    <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-2 bg-white border border-divider rounded-md focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition-colors" data-id="element-73" />
                  </div>
                </div>

                <div className="space-y-1" data-id="element-74">
                  <label htmlFor="email" className="text-sm font-medium text-foreground/80" data-id="element-75">
                    Email *
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full px-4 py-2 bg-white border border-divider rounded-md focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition-colors" data-id="element-76" />
                </div>

                <div className="space-y-1" data-id="element-77">
                  <label htmlFor="phone" className="text-sm font-medium text-foreground/80" data-id="element-78">
                    Phone Number *
                  </label>
                  <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-2 bg-white border border-divider rounded-md focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition-colors" data-id="element-79" />
                </div>

                <div className="space-y-4 pt-4 border-t border-divider" data-id="element-80">
                  <label className="flex items-start gap-3 cursor-pointer group" data-id="element-81">
                    <div className="relative flex items-center mt-1" data-id="element-82">
                      <input type="checkbox" name="consentTransactional" required checked={formData.consentTransactional} onChange={handleInputChange} className="peer sr-only" data-id="element-83" />
                      <div className="w-5 h-5 border-2 border-divider rounded bg-white peer-checked:bg-gold peer-checked:border-gold transition-colors flex items-center justify-center" data-id="element-84">
                        <svg className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3" data-id="element-85">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-86" />
                        </svg>
                      </div>
                    </div>
                    <span className="text-xs text-foreground/70 leading-relaxed group-hover:text-foreground transition-colors" data-id="element-87">
                      By checking this box, I consent to receive non-marketing
                      text messages from Be Clinical about appointment
                      confirmations, service updates, and scheduling. Message
                      frequency varies. Message & data rates may apply. Reply
                      STOP to unsubscribe at any time. *
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group" data-id="element-88">
                    <div className="relative flex items-center mt-1" data-id="element-89">
                      <input type="checkbox" name="consentMarketing" checked={formData.consentMarketing} onChange={handleInputChange} className="peer sr-only" data-id="element-90" />
                      <div className="w-5 h-5 border-2 border-divider rounded bg-white peer-checked:bg-gold peer-checked:border-gold transition-colors flex items-center justify-center" data-id="element-91">
                        <svg className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3" data-id="element-92">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-93" />
                        </svg>
                      </div>
                    </div>
                    <span className="text-xs text-foreground/70 leading-relaxed group-hover:text-foreground transition-colors" data-id="element-94">
                      By checking this box, I consent to receive marketing text
                      messages from Be Clinical including special offers,
                      discounts, and promotional updates at the phone number
                      provided. Frequency may vary. Message & data rates may
                      apply. Reply STOP to unsubscribe at any time.
                    </span>
                  </label>
                </div>

                <div className="pt-4" data-id="element-95">
                  <Button type="submit" fullWidth data-id="element-96">
                    Continue to Booking
                  </Button>
                </div>
              </form>
            </div>
          </>}
      </div>
    </div>;
}